// LoadTemplateModal.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  InputAdornment,
  MuiList as List,
  ListItemButton,
  ListItemText,
  Chip,
  Typography,
  CircularProgress,
  Divider,
  Alert,
} from "components/mui";
import SearchIcon from "@mui/icons-material/Search";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useNavigate } from "react-router-dom";
import { ROUTE_EMAIL_TEMPLATES } from "constants/routerPath";
import { GetEmailTemplates } from "api/canmail/CANmailAPI";
import TemplatePreview from "containers/pages/CANmailTemplates/components/templatePreview/TemplatePreview";

const editorTypeOf = (t) => t.editor_type || (t.template_content_drop ? "drop" : "text");

/**
 * Browse-and-preview template picker for the Email Sender. Lets the user search,
 * preview, and load a template — and hands the loaded template's own editor type
 * back so the composer opens it in the correct editor. Full template management
 * lives on the Template Manager page (linked from the footer).
 */
function LoadTemplateModal({ open, onClose, onLoadTemplate }) {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (open) {
      setSearch("");
      setSelectedId(null);
      fetchTemplates();
    }
  }, [open]);

  const fetchTemplates = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const data = await GetEmailTemplates();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("[LoadTemplateModal] Error fetching templates:", error);
      setTemplates([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return templates;
    return templates.filter((t) =>
      [t.template_name, t.description, t.folder, t.tags]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [templates, search]);

  const selected = templates.find((t) => t.template_id === selectedId) || null;

  const handleLoad = () => {
    if (!selected) return;
    const editorType = editorTypeOf(selected);
    onLoadTemplate?.({
      content:
        editorType === "drop"
          ? selected.template_content_drop
          : selected.template_content_text,
      editorType,
      templateId: selected.template_id,
      templateName: selected.template_name,
    });
    onClose();
  };

  const goToManager = () => {
    onClose();
    navigate(ROUTE_EMAIL_TEMPLATES);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Load Template</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, minHeight: 420 }}>
          {/* Template list */}
          <Box
            sx={{
              width: { xs: "100%", sm: 300 },
              borderRight: { sm: 1 },
              borderColor: { sm: "divider" },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ p: 1.5 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search templates…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: 1, overflowY: "auto", maxHeight: 460 }}>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : loadError ? (
                <Box sx={{ p: 2 }}>
                  <Alert severity="error" action={<Button size="small" onClick={fetchTemplates}>Retry</Button>}>
                    Could not load templates.
                  </Alert>
                </Box>
              ) : filtered.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No templates found.
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {filtered.map((t) => (
                    <React.Fragment key={t.template_id}>
                      <ListItemButton
                        selected={t.template_id === selectedId}
                        onClick={() => setSelectedId(t.template_id)}
                      >
                        <ListItemText primary={t.template_name} secondary={t.folder || null} />
                        <Chip
                          label={editorTypeOf(t) === "drop" ? "Visual" : "Text"}
                          size="small"
                          variant="outlined"
                        />
                      </ListItemButton>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          </Box>

          {/* Preview */}
          <Box sx={{ flex: 1, minWidth: 0, p: 1.5 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Preview
            </Typography>
            <Box sx={{ height: 400, overflow: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}>
              {selected ? (
                <TemplatePreview template={selected} />
              ) : (
                <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Select a template to preview it.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 2 }}>
        <Button startIcon={<SettingsOutlinedIcon />} onClick={goToManager} color="primary">
          Manage templates
        </Button>
        <Box>
          <Button onClick={onClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleLoad} variant="contained" disabled={!selected} sx={{ ml: 1 }}>
            Load
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default LoadTemplateModal;
